import { trackEvent } from "@hopper-b2b/api";
import { getLang } from "@hopper-b2b/i18n";
import { useScript } from "@hopper-b2b/utilities";
import { useCallback, useEffect, useState } from "react";
import {
  AuthEvents,
  AuthMachineEventType,
  useAuthState,
} from "../../../../machine";
import {
  AuthTrackingEvents,
  AuthType,
  ChoseSignupEvent,
} from "../../../../types/tracking";

interface GoogleLoginResponse {
  credential: string; // idToken field in /create/session request
}

const GOOGLE_POLL_INTERVAL_MS = 100;
const GOOGLE_POLL_MAX_ATTEMPTS = 50; // 5 seconds max

export const GoogleLogin = ({
  googleIdentityClientId,
}: {
  googleIdentityClientId: string;
}) => {
  const locale = getLang();

  const [buttonDiv, setButtonDiv] = useState<HTMLDivElement | null>(null);

  const [, send] = useAuthState<AuthEvents, unknown>();

  // Inject the Google Identity Services script tag into <head>.
  // We don't rely on the onLoad callback for timing — instead we poll
  // for window.google.accounts.id below, which is resilient to script
  // caching and Helmet deduplication.
  const scriptElement = useScript({
    src: `https://accounts.google.com/gsi/client?hl=${locale}`,
    onLoad: () => {
      // no-op: polling handles detection
    },
  });

  const handleCredentialResponse = useCallback(
    (response: GoogleLoginResponse) => {
      send({
        type: AuthMachineEventType.GOOGLE_SIGN_IN,
        idToken: response.credential,
      });
    },
    [send]
  );

  const buttonRef = useCallback((node: HTMLDivElement | null) => {
    setButtonDiv(node);
  }, []);

  const onClickHandler = useCallback(() => {
    trackEvent({
      eventName: AuthTrackingEvents.ChoseSignup,
      properties: {
        auth_type: AuthType.Google,
      },
    } as ChoseSignupEvent);
  }, []);

  useEffect(() => {
    if (!googleIdentityClientId || !buttonDiv) {
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryRender = () => {
      if (cancelled) return;

      if (!window?.google?.accounts?.id) {
        attempts++;
        if (attempts < GOOGLE_POLL_MAX_ATTEMPTS) {
          setTimeout(tryRender, GOOGLE_POLL_INTERVAL_MS);
        }
        return;
      }

      window.google.accounts.id.initialize({
        client_id: googleIdentityClientId,
        callback: handleCredentialResponse,
        locale: getLang(),
      });
      window.google.accounts.id.renderButton(buttonDiv, {
        theme: "outline",
        size: "large",
        longtitle: true,
        width: buttonDiv.offsetWidth,
        click_listener: onClickHandler,
        locale: getLang(),
      });
    };

    tryRender();

    return () => {
      cancelled = true;
    };
  }, [
    googleIdentityClientId,
    handleCredentialResponse,
    onClickHandler,
    buttonDiv,
  ]);

  return (
    googleIdentityClientId && (
      <>
        {scriptElement}
        <div ref={buttonRef} style={{ width: "100%" }} />
      </>
    )
  );
};
